import React from "react";
import { useNavigate } from "react-router-dom";
import { Coins, Command, LogOut } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TEST_IDS } from "@/constants/testIds";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";

export default function Header({ onOpenCmdk }) {
    const { user, logout } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();

    const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

    async function handleLogout() {
        await logout();
        navigate("/");
    }

    return (
        <header className="sticky top-0 z-30 h-16 border-b border-white/8 bg-brand-void/75 backdrop-blur-xl">
            <div className="h-full px-6 flex items-center justify-between gap-4">
                <button
                    type="button"
                    onClick={onOpenCmdk}
                    data-testid={TEST_IDS.app.headerCmdk}
                    className="flex-1 max-w-xl flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/[0.08] border border-white/8 hover:border-white/15 transition text-left"
                >
                    <span className="text-sm text-zinc-500">{t.app.searchHint}</span>
                    <span className="flex items-center gap-1 text-xs text-zinc-500 font-mono">
                        <Command size={12} />K
                    </span>
                </button>

                <div className="flex items-center gap-3">
                    <div
                        data-testid={TEST_IDS.app.headerCredits}
                        className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full badge-cyan text-xs font-semibold font-mono"
                    >
                        <Coins size={14} />
                        <span>{user?.credits ?? 0}</span>
                        <span className="text-zinc-400 font-sans">{t.app.credits}</span>
                    </div>

                    <LanguageSwitcher testId={TEST_IDS.app.headerLang} />

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                data-testid={TEST_IDS.app.headerUser}
                                className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-indigo to-brand-violet flex items-center justify-center text-white text-xs font-bold ring-1 ring-white/10 hover:ring-white/25 transition overflow-hidden"
                            >
                                {user?.picture ? (
                                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                                ) : (
                                    initials
                                )}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 bg-brand-surface border-white/10 text-white">
                            <DropdownMenuLabel>
                                <div className="text-sm text-white truncate">{user?.name}</div>
                                <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem onClick={() => navigate("/app/settings")} className="focus:bg-white/5">
                                {t.nav.settings}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate("/app/billing")} className="focus:bg-white/5">
                                {t.nav.billing}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem
                                onClick={handleLogout}
                                data-testid={TEST_IDS.app.logout}
                                className="focus:bg-white/5 text-red-300"
                            >
                                <LogOut size={14} className="mr-2" /> {t.nav.logout}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        </header>
    );
}
